import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useListProjects } from "~/api/folders/folders";
import { useCompleteFtue } from "~/api/organizations/organizations";
import {
  getGetCurrentUserQueryKey,
  useGetCurrentUser,
} from "~/api/users/users";
import { useOrganizationParams } from "~/helpers";
import { trackEvent } from "~/utils/analytics";
import logger from "~/utils/logger";

export type FtueCompletionMethod = "create_case" | "explore_demo" | "skip";

export const useFtue = () => {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganizationParams();
  const { data: user, isLoading: isUserLoading } = useGetCurrentUser();
  const { mutateAsync: completeFtueMutation, isPending: isCompletingFtue } =
    useCompleteFtue();

  const { data: casesData, isLoading: isCasesLoading } = useListProjects(
    organizationId,
    { archived: false, limit: 50 },
    { query: { enabled: !!organizationId } }
  );

  const allCases = casesData?.projects || [];
  const demoCase = allCases.find((c) => c.isDemo);
  const hasRealCases = allCases.some((c) => !c.isDemo);

  const isLoading = isUserLoading || isCasesLoading;
  const shouldShowFtue =
    !isLoading && user && !user.hasCompletedFtue && !hasRealCases;

  const completeFtue = useCallback(
    async (method: FtueCompletionMethod) => {
      try {
        await completeFtueMutation();
        trackEvent({
          eventName: "ftue_completed",
          properties: { method },
        });
        await queryClient.invalidateQueries({
          queryKey: getGetCurrentUserQueryKey(),
        });
      } catch (error) {
        logger.error("Failed to complete FTUE:", error);
      }
    },
    [completeFtueMutation, queryClient]
  );

  const trackFtueEvent = useCallback(
    (
      eventName:
        | "ftue_welcome_viewed"
        | "ftue_create_case_clicked"
        | "ftue_explore_demo_clicked"
        | "ftue_skipped"
    ) => {
      trackEvent({ eventName });
    },
    []
  );

  return {
    user,
    isLoading,
    shouldShowFtue,
    completeFtue,
    isCompletingFtue,
    trackFtueEvent,
    demoCase,
  };
};
